//asset CRUD + upload path (assets list, categories, collections, submitAssetFromForm, etc.). It’s task oriented, per-user, often scoped to screens that manipulate assets.

import React, { createContext, useState, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  fetchAssets,
  saveMetadataToFirestore,
  updateAssetMetadata,
  deleteAsset,
  ensureDefaultFolder,
  fetchCollections,
  saveCategoryToFirestore,
  uploadAssetWithDefaultFolder,
} from '../utils/frontend_assetManager';
import { processAssetForm } from '../utils/assetuploadform/processAssetForm';
import { useAuth } from './AuthContext';
import { resolveAssetVersionInfo } from '../utils/assetVersioning';

/* ──────────────────────────────────────────────────────────────
   SAFE DEFAULTS
   • Prevents crashes on routes where the provider isn’t mounted
   • submitAssetFromForm is a no-op so destructuring never throws
   ────────────────────────────────────────────────────────────── */
const AssetMetadataDefault = {
  metadataFields: [],
  categories: [],
  assets: [],
  collections: [],
  loading: false,
  error: null,
  fetchMetadata: async () => {},
  fetchAllAssets: async () => {},
  fetchAllCollections: async () => {},
  saveNewAssetMetadata: async () => {},
  saveMetadataField: async () => {},
  saveCategory: async () => {},
  submitAssetFromForm: async () => null,

  // ENTERPRISE: runtime cache safe defaults
  upsertAssetRuntime: () => {},
  getAssetRuntime: () => null,
  clearAssetRuntime: () => {},
};

const AssetMetadataContext = createContext(AssetMetadataDefault);

/**
 * Custom hook to use the AssetMetadataContext
 * @returns {Object} Context value for asset metadata operations
 */
export const useAssetMetadata = () => useContext(AssetMetadataContext);

export const AssetMetadataProvider = ({ children }) => {
  const { organizationId, currentUser, userPermissions } = useAuth();
  const [metadataFields, setMetadataFields] = useState([]);
  const [categories, setCategories] = useState([]);
  const [assets, setAssets] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ──────────────────────────────────────────────────────────────
  // ENTERPRISE: runtime asset cache (UI-only, non-persistent)
  // Purpose:
  // - Store resolved preview meta keyed by assetId (and/or local temp id)
  // - Power "system fields" (asset.versionNumber, dims, type) without
  //   polluting answers or relying on labels
  // - Works across AUF schemas per organization
  // ──────────────────────────────────────────────────────────────
  const [assetRuntimeById, setAssetRuntimeById] = useState({});

  // Merge patch into runtime cache (idempotent + safe)
  const upsertAssetRuntime = useCallback((assetId, patch) => {
    if (!assetId || !patch || typeof patch !== 'object') return;

    setAssetRuntimeById((prev) => {
      const curr = prev?.[assetId] || {};
      // shallow merge is enough because previewMeta is flat-ish in your usage
      const next = { ...curr, ...patch };

      // No-op if nothing changed (prevents re-render storms)
      const keys = new Set([...Object.keys(curr), ...Object.keys(patch)]);
      for (const k of keys) {
        if (curr[k] !== next[k]) {
          return { ...(prev || {}), [assetId]: next };
        }
      }
      return prev;
    });
  }, []);

  const getAssetRuntime = useCallback((assetId) => {
    if (!assetId) return null;
    return assetRuntimeById?.[assetId] || null;
  }, [assetRuntimeById]);

  const clearAssetRuntime = useCallback((assetId) => {
    if (!assetId) return;
    setAssetRuntimeById((prev) => {
      if (!prev || !prev[assetId]) return prev;
      const next = { ...prev };
      delete next[assetId];
      return next;
    });
  }, []);

  /**
   * Fetch metadata fields and categories for the organization.
   */
  const fetchMetadata = useCallback(async (organizationId) => {
    if (!organizationId) {
      setError('Organization ID is missing.');
      console.error('[AssetMetadataContext] Organization ID is required to fetch metadata.');
      return;
    }

    if (categories.length > 0 && metadataFields.length > 0) {
      console.log('[AssetMetadataContext] Metadata already loaded. Skipping fetch.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[AssetMetadataContext] Fetching metadata for organization: ${organizationId}`);
      const fetchedCategories = await fetchCollections(organizationId);
      setCategories(fetchedCategories || []);
      const fetchedMetadataFields = fetchedCategories.flatMap((collection) => collection.metadataFields || []);
      setMetadataFields(fetchedMetadataFields || []);
      console.log('[AssetMetadataContext] Fetched categories:', fetchedCategories);
      console.log('[AssetMetadataContext] Fetched metadata fields:', fetchedMetadataFields);
    } catch (err) {
      console.error('[AssetMetadataContext] Error fetching metadata:', err);
      setError(err.message || 'Failed to fetch metadata.');
    } finally {
      setLoading(false);
    }
  }, [categories.length, metadataFields.length]);

  /**
   * Save a category to Firestore.
   */
  const saveCategory = useCallback(async (organizationId, categoryData) => {
    if (!organizationId || !categoryData) {
      throw new Error('Organization ID and category data are required.');
    }

    console.log('[AssetMetadataContext] Saving category:', categoryData);

    try {
      setLoading(true);
       const savedCategory = await saveCategoryToFirestore({
         organizationId,
         userId: currentUser?.uid,
         ...categoryData,
       });
      setCategories((prevCategories) => [...prevCategories, savedCategory]);
      console.log('[AssetMetadataContext] Category saved:', savedCategory);
    } catch (err) {
      console.error('[AssetMetadataContext] Error saving category:', err);
      setError(err.message || 'Failed to save category.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save a metadata field to Firestore.
   */
  const saveMetadataField = useCallback(async (organizationId, fieldData) => {
    if (!organizationId || !fieldData) {
      throw new Error('Organization ID and field data are required.');
    }

    console.log('[AssetMetadataContext] Saving metadata field:', fieldData);

    try {
      setLoading(true);
      const savedField = await saveMetadataToFirestore({ organizationId, ...fieldData });
      setMetadataFields((prevFields) =>
        prevFields.some((field) => field.id === savedField.id)
          ? prevFields.map((field) => (field.id === savedField.id ? savedField : field))
          : [...prevFields, savedField]
      );
      console.log('[AssetMetadataContext] Metadata field saved:', savedField);
    } catch (err) {
      console.error('[AssetMetadataContext] Error saving metadata field:', err);
      setError(err.message || 'Failed to save metadata field.');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch all assets for the current organization.
   */
  const fetchAllAssets = useCallback(async (filters = {}) => {
    if (!organizationId || !currentUser) {
      setError('Organization or user details are missing.');
      console.error('[AssetMetadataContext] Organization ID or current user is missing.');
      return;
    }

      setLoading(true);
      setError(null);

      try {


      console.log('[AssetMetadataContext] Fetching all assets with filters:', filters);
      const authToken = await currentUser.getIdToken();
      const response = await fetchAssets(
        organizationId,
        filters.collectionId || null,
        filters.tags || [],
        filters.searchQuery || '',
        filters.pageSize || 20,
        filters.lastVisible || null,
        authToken,

        // OPTIONALS (supported by your frontend_assetManager.fetchAssets)
        filters.formIdFilter || null,
        filters.assetType || null,
        filters.includeUrls !== false,
        filters.includeCount === true,

        // AUF fast filters payload:
        // { equals, equalsAny, arrayAny, dateRange }
        filters.promotedFilters || null
      );

      setAssets(response.assets || []);
      console.log('[AssetMetadataContext] Fetched assets:', response.assets);
    } catch (err) {
      console.error('[AssetMetadataContext] Error fetching assets:', err);
      setError(err.message || 'Failed to fetch assets.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, currentUser]);

  /**
   * Fetch all collections (folders) for the current organization.
   */
  const fetchAllCollections = useCallback(async () => {
    if (!organizationId) {
      setError('Organization ID is missing.');
      console.error('[AssetMetadataContext] Organization ID is required to fetch collections.');
      return;
    }

    if (collections.length > 0) {
      console.log('[AssetMetadataContext] Collections already loaded. Skipping fetch.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log(`[AssetMetadataContext] Fetching collections for organization: ${organizationId}`);
      const collectionsData = await fetchCollections(organizationId);
      setCollections(collectionsData || []);
      console.log('[AssetMetadataContext] Fetched collections:', collectionsData);
    } catch (err) {
      console.error('[AssetMetadataContext] Error fetching collections:', err);
      setError(err.message || 'Failed to fetch collections.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, collections.length]);

  /**
   * Save metadata for a new asset to Firestore.
   */
  const saveNewAssetMetadata = useCallback(async (metadata) => {
    if (!organizationId || !currentUser) {
      setError('Organization or user details are missing.');
      console.error('[AssetMetadataContext] Organization ID or current user is required to save metadata.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('[AssetMetadataContext] Saving new asset metadata:', metadata);
      const defaultFolderId = await ensureDefaultFolder(organizationId, currentUser.uid);
      const metadataWithFolder = { ...metadata, collectionId: metadata.collectionId || defaultFolderId };
      const savedMetadata = await saveMetadataToFirestore(metadataWithFolder);
      setAssets((prevAssets) => [savedMetadata, ...prevAssets]);
      console.log('[AssetMetadataContext] Saved metadata:', savedMetadata);
    } catch (err) {
      console.error('[AssetMetadataContext] Error saving asset metadata:', err);
      setError(err.message || 'Failed to save metadata.');
    } finally {
      setLoading(false);
    }
  }, [organizationId, currentUser]);

/**
 * Submit a new asset using the published Asset Upload Form.
 * 1) Normalize & validate answers (dry run – no form actions/writes)
 * 2) Build additionalMetadata for backend (responses + dynamic tags + formId)
 * 3) Upload via updated uploader (large/small handled; backend writes asset doc)
 * 4) Update local assets state
 *
 * @param {Object}   params
 * @param {File|Blob|string} params.file            – File/Blob (or base64 string for screenshots)
 * @param {string}   params.formId                  – Published Asset Upload Form id
 * @param {Object}   params.layoutConfig            – From FormLayoutContext
 * @param {Object}   params.fieldsMetadata          – From FieldMetadataContext (map by fieldId)
 * @param {Object}   params.dropdownOptions         – From MultiAnswerFieldContext (map by optionsSetUFI)
 * @param {Object}   [params.fieldChanges]          – Optional, from FieldEditContext
 * @param {Object}   [params.validationErrors]      – Optional, from FieldEditContext
 * @param {string}   [params.collectionId]          – Optional folder; default will be ensured when null
 * @param {string}   [params.assetType]             – 'icon' | 'banner' | 'screenshot' | null
 * @param {string}   [params.overrideName]          – Optional file name override
 */
const submitAssetFromForm = useCallback(async ({
  file,
  formId,
  layoutConfig,
  fieldsMetadata,
  dropdownOptions,
  fieldChanges = {},
  validationErrors = {},
  collectionId = null,
  assetType = null,
  overrideName = null,
  submissionData = undefined, 
}) => {
  if (!organizationId || !currentUser) {
    setError('Organization or user details are missing.');
    console.error('[AssetMetadataContext] Missing organizationId or currentUser in submitAssetFromForm.');
    return null;
  }

  // 🔒 Preflight: require upload permission (surfaced early in UX)
  if (!Array.isArray(userPermissions) || !userPermissions.includes('uploadAsset')) {
    const msg = 'You do not have permission to upload assets.';
    console.error('[AssetMetadataContext] Permission check failed:', msg);
    setError(msg);
    return null;
  }

  // 🔎 Guard: file must be present
  if (!file) {
    const msg = 'Please choose a file to upload.';
    console.error('[AssetMetadataContext] Missing file in submitAssetFromForm.');
    setError(msg);
    return null;
  }

setLoading(true);
setError(null);

// hoisted so catch/finally can see them
let localUrl = null;
let tempId   = null;

try {
    // 0) Normalize & validate (dry-run – mirrors FormActionsContext without actions/writes)
    console.log('[AssetMetadataContext] Normalizing asset-upload answers (dry run)…');

const {
  submissionId,
  submissionData: normalizedSubmission, // avoid name shadowing if you care
  flattenedSubmission,
  nestedResponses,
  isValid,
  errors,
} = await processAssetForm({
  formId,
  submissionData, // ← use what PublishedFormPage sent (buildSubmissionArray())
  layoutConfig,
  fieldsMetadata,
  dropdownOptions,
  fieldChanges,
  validationErrors,
});

// (optional debug)
console.log(
  '[AssetMetadataContext] processAssetForm source:',
  submissionData ? 'AnswersContext submissionData' : 'rebuilt from layout/fieldChanges'
);


    if (!isValid) {
      console.error('[AssetMetadataContext] Asset form invalid. Errors:', errors);
      setError('Asset Upload Form has validation errors. Please review your inputs.');
      setLoading(false);
      return null;
    }

// ─────────────────────────────────────────────────────────────
// (A) Build a JSON-safe responses object (strip File/Blob)
// ─────────────────────────────────────────────────────────────
const cleanedResponses = Object.fromEntries(
  Object.entries(nestedResponses).map(([ufi, entry]) => {
    if (ufi.startsWith('label_')) return [ufi, entry]; // labels stay as-is
    const v = entry?.value;
    const looksLikeAttach =
      v && typeof v === 'object' && (('file' in v) || ('filename' in v) || ('assetId' in v));
    if (looksLikeAttach) {
      // keep a simple identifier for BI/debug; backend ignores attach UFI for promotion
      const simple = v?.name || v?.filename || v?.assetId || null;
      return [ufi, { value: simple }];
    }
    return [ufi, entry];
  })
);

// ─────────────────────────────────────────────────────────────
// (B) Compute tags from cleanedResponses ONLY, and skip attach/image UFIs
//     1) Find all attach/image UFIs from the current form metadata
//     2) For dropdown fields, map optionId(s) → labels via dropdownOptions
//     3) For text/number fields, use the raw value
// ─────────────────────────────────────────────────────────────
const attachUfis = Object.values(fieldsMetadata || {})
  .filter((m) => m?.type === 'attach' || m?.type === 'image')
  .map((m) => m?.ufi)
  .filter(Boolean);

const dropdownIndex = Object.values(fieldsMetadata || {}).reduce(
  (acc, meta) => {
    if (!meta?.ufi) return acc;
    const isDropdown =
      meta.type === 'single-select' || meta.type === 'multi-select';
    if (!isDropdown || !meta.optionsSetUFI) return acc;

    const setArr = Array.isArray(dropdownOptions?.[meta.optionsSetUFI])
      ? dropdownOptions[meta.optionsSetUFI]
      : [];

    acc[meta.ufi] = {
      optionsSetUFI: meta.optionsSetUFI,
      // Store the full option object so we can read both label + value later
      byId: new Map(
        setArr.map((opt) => [opt.optionId, opt])
      ),
    };
    return acc;
  },
  {}
);

const derivedTags = Object.entries(cleanedResponses)
  .filter(([ufi]) => !ufi.startsWith('label_'))
  .filter(([ufi]) => !attachUfis.includes(ufi)) // ← skip attach/image UFIs for tags
  .flatMap(([ufi, obj]) => {
    const v = obj?.value;
    const dropdownMeta = dropdownIndex[ufi];

    if (dropdownMeta) {
      const { byId } = dropdownMeta;
      const mapOne = (optId) => {
        if (!optId) return null;
        const opt   = byId.get(optId);
        const label = opt?.label || String(optId);
        const s = String(label).trim();
        return s || null;
      };


      if (Array.isArray(v)) {
        return v
          .map(mapOne)
          .filter(Boolean);
      }
      return mapOne(v) ? [mapOne(v)] : [];
    }

    // Non-dropdown: keep previous behavior
    if (Array.isArray(v)) {
      return v
        .filter((x) => typeof x === 'string' || typeof x === 'number')
        .map((x) => (typeof x === 'number' ? String(x) : x))
        .map((x) => x.trim())
        .filter(Boolean);
    }
    if (typeof v === 'string') {
      const s = v.trim();
      return s ? [s] : [];
    }
    if (typeof v === 'number') {
      return [String(v)];
    }
    return [];
  });

console.log(
  '[AssetMetadataContext] derivedTags (dynamic from answers, cleaned):',
  derivedTags
);

// NEW: derive canonical assetType from AUF field (if present)
let semanticAssetType = null;

// 1) find a field tagged as the canonical asset type
const assetTypeFieldMeta = Object.values(fieldsMetadata || {}).find(
  (m) =>
    m?.assetFieldRole === 'assetType' ||
    m?.semanticKey === 'asset.type'
);

if (assetTypeFieldMeta?.ufi) {
  const atUfi = assetTypeFieldMeta.ufi;
  const resp  = cleanedResponses?.[atUfi];
  const rawOptId = resp && resp.value; // optionId from processAssetForm

  const dropdownMeta = dropdownIndex[atUfi];
  if (dropdownMeta && rawOptId) {
    const opt = dropdownMeta.byId.get(rawOptId);
    if (opt) {
      // Prefer canonical 'value' (icon/banner/screenshot/…), else fallback
      semanticAssetType =
        opt.value ||
        (opt.label && opt.label.toLowerCase()) ||
        null;
    }
  }
}

// Fall back to the incoming assetType arg for basic flows (ImageFieldComponent,
// screenshots, etc.) when there is no AUF field or no answer yet.
const finalAssetType =
  semanticAssetType != null ? semanticAssetType : assetType || null;


// Canonical file name for server-side version buckets.
const lowerName = (
  overrideName || (file && file.name) || ''
).trim().toLowerCase();

// 2) Prepare additionalMetadata for backend
const additionalMetadata = {
  formId,
  status: 'published',
  isLatestVersion: true,
  tags: derivedTags,
  responses: cleanedResponses,
  ingestSubmissionId: submissionId,
  canonicalName: lowerName,
};


// (Optional) size hint (KB) so size isn’t null in docs for large-file path
if (file && typeof file.size === 'number') {
  additionalMetadata.size = Math.round(file.size / 1024);
}


    console.log('[AssetMetadataContext] additionalMetadata prepared for uploader:', additionalMetadata);

    // Silent versioning hint: resolve likely previous version on client.
    // Backend remains authoritative (Step 2 resolves & transacts).
    let versionMeta = {};
    try {
    versionMeta = await resolveAssetVersionInfo({
      assetType      : finalAssetType,
      organizationId : organizationId,
      fileName       : lowerName,
      formId         : formId,
      status         : 'published',
    });

    } catch (e) {
      console.warn(
        '[AssetMetadataContext] version hint resolve failed:',
        e?.message || e
      );
    }


// 3) Optimistic local preview + upload + finalize swap
const authToken = await currentUser.getIdToken();
console.log('[AssetMetadataContext] Calling uploadAssetWithDefaultFolder…');

//  SUBMIT TIMER (client): start upload timing
const __t_upload_start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
console.log('[SUBMIT:TIMER][client] uploadAssetWithDefaultFolder START', {
  formId,
  organizationId,
  fileName: overrideName || (file && file.name) || null,
  fileSize: (file && typeof file.size === 'number') ? file.size : null,
  assetType: finalAssetType || null,
});

// 3A) Local preview (File/Blob only). For base64 screenshots, skip.
  const isBlob =
    ((typeof File !== 'undefined') && (file instanceof File)) ||
    ((typeof Blob !== 'undefined') && (file instanceof Blob));

  if (isBlob) {
    try {
      localUrl = URL.createObjectURL(file);
    } catch (e) {
      console.warn(
        '[AssetMetadataContext] createObjectURL failed (non-fatal):',
        e?.message || e
      );
    }
  }

  // push a temporary card so the grid feels instant
  tempId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  if (localUrl) {
    setAssets((prev) => ([
      {
        id: tempId,
        name: overrideName || (file && file.name) || 'upload',
        status: 'processing',
        localSrc: localUrl,
        assetType: finalAssetType || null, // see next section
        tags: derivedTags || [],
      },
      ...prev,
    ]));
  }

  // 3B) Do the upload …
const meta = await uploadAssetWithDefaultFolder(
  file,
  organizationId,
  currentUser.uid,
  { ...additionalMetadata, ...versionMeta },
  collectionId,
  userPermissions || [],
  finalAssetType,
  authToken,
  overrideName
);

// stamp version hint onto placeholder meta so UI/submission
// does NOT need to call getAssetById just to learn Version #.
// This keeps AUF fast + consistent while the worker finalizes.
// NOTE: backend remains authoritative; this is an enterprise-safe UX hint.
try {
  const hintedVersion =
    (versionMeta && typeof versionMeta.versionNumber === 'number')
      ? versionMeta.versionNumber
      : null;

  const metaHasValidVersion =
    meta && typeof meta.versionNumber === 'number' && meta.versionNumber > 0;

  if (meta && !metaHasValidVersion && hintedVersion && hintedVersion > 0) {
    meta.versionNumber = hintedVersion;

    // Optional: preserve linkage hints too (helpful for audit/debug)
    if (versionMeta?.previousAssetId && !meta.previousAssetId) {
      meta.previousAssetId = versionMeta.previousAssetId;
    }

    console.log('[AssetMetadataContext] Applied client version hint to returned meta:', {
      assetId: meta?.id || meta?.assetId || null,
      versionNumber: meta.versionNumber,
      previousAssetId: meta?.previousAssetId || null,
      status: meta?.status || null,
    });
  }
} catch (e) {
  console.warn('[AssetMetadataContext] Failed to apply version hint (non-fatal):', e?.message || e);
}

// SUBMIT TIMER (client): upload returned (200 or 202 placeholder)
const __t_upload_end = (typeof performance !== 'undefined' ? performance.now() : Date.now());
console.log('[SUBMIT:TIMER][client] uploadAssetWithDefaultFolder END', {
  ms: Math.round(__t_upload_end - __t_upload_start),
  status: meta?.status || null,
  assetId: meta?.id || meta?.assetId || null,
});
console.log(
  '[AssetMetadataContext] Asset uploaded successfully. Metadata:',
  meta
);

// 3C) Replace temp card with server doc (202 placeholder or 200 final)
const serverId = meta.id || meta.assetId;

// If we showed a local temp card, replace it with the returned doc
if (localUrl) {
  setAssets((prev) => {
    const rest = prev.filter((a) => a.id !== tempId);
    return [
      {
        ...meta,
        id: serverId,
        // keep showing the local preview until cloud thumb exists
        localSrc: localUrl,
      },
      ...rest,
    ];
  });
} else {
  // no local card (e.g., base64 screenshot) – prepend normally
  setAssets((prev) => [meta, ...prev]);
}

// 3D) If backend returned a placeholder (202), wait for final doc and swap
if (meta.status === 'processing' && typeof meta.__waitForFinal === 'function') {
  try {
    // ✅ ENTERPRISE: do NOT block AUF submit on worker finalization.
    // Kick off background upgrade and return immediately.
    const __t_finalize_start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    console.log('[SUBMIT:TIMER][client] worker finalization WAIT (background) START', {
      assetId: serverId,
    });

    (async () => {
      try {
        const finalDoc = await meta.__waitForFinal();
        const __t_finalize_end = (typeof performance !== 'undefined' ? performance.now() : Date.now());

        console.log('[SUBMIT:TIMER][client] worker finalization WAIT (background) END', {
          assetId: serverId,
          ms: Math.round(__t_finalize_end - __t_finalize_start),
          finalStatus: finalDoc?.status || null,
        });

        const cloud400 = finalDoc?.thumb?.urls?.['400'] || null;

        // swap to cloud; keep localSrc until the card actually paints the cloud
        // ENTERPRISE: do NOT write Storage paths into cloudSrc.
        // finalDoc.thumb.urls["400"] is a STORAGE PATH, not a browser-usable URL.
        // AssetCard will resolve a signed preview URL from the server when needed.
        setAssets((prev) =>
          prev.map((a) =>
            a.id === serverId
              ? {
                  ...a,
                  ...finalDoc,
                  cloudSrc: null,
                  localSrc: a.localSrc, // revoke after paint (see AssetCard)
                }
              : a
          )
        );

        // let the <img> render cloud first, then revoke blob & clear localSrc
        if (localUrl) {
          setTimeout(() => {
            try { URL.revokeObjectURL(localUrl); } catch (_) {}
            setAssets((prev) =>
              prev.map((a) =>
                a.id === serverId ? { ...a, localSrc: null } : a
              )
            );
          }, 300);
        }
      } catch (e) {
        console.warn('[AssetMetadataContext] background finalize wait failed (non-fatal):', e?.message || e);
      }
    })();

    // ✅ IMPORTANT: DO NOT return early here.
    // Let submitAssetFromForm() finish its normal flow and return meta
    // from the shared return at the bottom of the try.
  } catch (e) {
    console.warn('[AssetMetadataContext] finalize launch failed (non-fatal):', e?.message || e);
    // ✅ Also do NOT return early here; fall through to the normal return.
  }
}

// 3E) Legacy 200 path: already processed — optional early swap & revoke
const cloud400 = meta?.thumb?.urls?.['400'] || null;
if (localUrl && cloud400) {
  // give the img a tick to render the new URL then revoke the blob
  // ENTERPRISE: do NOT write Storage paths into cloudSrc.
  // meta.thumb.urls["400"] is a STORAGE PATH, not a browser URL.
  // AssetCard will resolve a signed preview URL from the server when needed.
  setTimeout(() => {
    try { URL.revokeObjectURL(localUrl); } catch (_) {}
    setAssets((prev) =>
      prev.map((a) =>
        a.id === serverId ? { ...a, localSrc: null, cloudSrc: null } : a
      )
    );
  }, 300);
}

return meta;


} catch (err) {
  console.error('[AssetMetadataContext] submitAssetFromForm error:', err);
  setError(err.message || 'Failed to submit asset.');

  // If we showed a temporary card, remove it and revoke the blob URL
  // so we don’t leak memory or leave a ghost card on error.
  try {
    if (localUrl) {
      try { URL.revokeObjectURL(localUrl); } catch (_) {}
      setAssets((prev) => prev.filter((a) => a.id !== tempId));
    }
  } catch (e2) {
    console.warn(
      '[AssetMetadataContext] temp cleanup failed (non-fatal):',
      e2?.message || e2
    );
  }

  return null;
} finally {
  setLoading(false);
}

}, [organizationId, currentUser, userPermissions, setError, setLoading, setAssets]);


  /**
   * Memoized context value to avoid unnecessary re-renders.
   */
const contextValue = useMemo(
  () => ({
    metadataFields,
    categories,
    assets,
    collections,
    loading,
    error,
    fetchMetadata,
    fetchAllAssets,
    fetchAllCollections,
    saveNewAssetMetadata,
    saveMetadataField,
    saveCategory, // Expose saveCategory
    submitAssetFromForm, // Expose DAM submission entry point

    // ENTERPRISE: runtime asset cache APIs (UI-only)
    upsertAssetRuntime,
    getAssetRuntime,
    clearAssetRuntime,
  }),
  [
    metadataFields,
    categories,
    assets,
    collections,
    loading,
    error,
    fetchMetadata,
    fetchAllAssets,
    fetchAllCollections,
    saveNewAssetMetadata,
    saveMetadataField,
    saveCategory,
    submitAssetFromForm,

    // include new deps
    upsertAssetRuntime,
    getAssetRuntime,
    clearAssetRuntime,
  ]
);

  return <AssetMetadataContext.Provider value={contextValue}>{children}</AssetMetadataContext.Provider>;
};

AssetMetadataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AssetMetadataProvider;
